/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { AssociatedDocument, TableDatabaseAdvanced } from '../../lib/wwg.classes';
import { DataService } from '../../sdk/services/core/data.service';
import { AuthService } from '../../sdk/services/core/auth.service';
import { DocumentApi } from '../../sdk/services';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { SpinnerComponent } from '../spinner/spinner.component';
import { NotificationService } from '../../services/notification.service';
import { DeleteConfirmDialogComponent } from '../../dialogs/delete-confirm-dialog/delete-confirm-dialog.component';

@Component({
  selector: 'wwg-filemanager',
  templateUrl: './filemanager.component.html',
  styleUrls: ['./filemanager.component.scss']
})
export class FilemanagerComponent implements OnInit {

  /**
   * Type of documents to be shown
   * @return {[type]} [description]
   */
  @Input()
  type: string;

  /**
   * Id of the owner of documents to be shown
   * @return {[type]} [description]
   */
  @Input()
  files: Array<AssociatedDocument> = [];

  /**
   * Stream owner of documents
   * @param  {DataService} privatedataService [description]
   * @return {[type]}                         [description]
   */
  owner: string;

  /**
   * Holder for documents to display
   * @return {[type]}                 [description]
   */
  tempDatabase: TableDatabaseAdvanced<AssociatedDocument> | null;

  loadedLength: number = 0;

  /**
   * Title and description modifications made to already saved files
   * [{
   *   id: The ID of the file
   *   title: The new title
   *   description: The new description
   * }]
   * @type {Array<object>}
   */
  modifiedData: Array<any>;

  /**
   * Document Table Definition
   * @type {Array<string>}
   */
  docsColumns: Array<string> = [
    'documentName',
    'size',
    'date',
    'title',
    'description',
    'action',
  ];

  userId: any;

  // Defines the table functionality
  @ViewChild('filter', { static: false }) filter: ElementRef;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  constructor(
    private DocumentApi: DocumentApi,
    private dataService: DataService,
    private authService: AuthService,
    private dialog: MatDialog,
    private notificationService: NotificationService,
  ) {
    this.owner = this.dataService.state.stream ? this.dataService.state.stream : this.dataService.state.id;
  }

  ngOnInit() {

    this.userId = this.authService.getUserId();

    const files = this.files;
    const documents = [];
    if (!Array.isArray(files)) {
      return;
    }

    files.forEach((item) => {
      if (!item.title || item.title === '' || item.title === 'undefined') {
        item.title = 'Click to set';
      }
      if (!item.description || item.description === '' || item.description === 'undefined') {
        item.description = 'Click to set';
      }

      documents.push({
        _id: item._id,
        name: item.metadata.name,
        type: item.metadata.mimetype,
        date: item.created,
        size: this.formatBytes(item.size, 2),
        title: item.title,
        description: item.description,
      });
    });

    // Sort the documents into date descending (newest at the top) order
    documents.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    this.paginator.length = this.loadedLength = documents.length;
    this.tempDatabase = new TableDatabaseAdvanced<AssociatedDocument>(documents, this.paginator, this.sort);
  }

  formatBytes(bytes: number, decimals: number) {

    if (typeof bytes !== 'number') {
      return '-';
    }

    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024,
      dm = decimals || 2,
      sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
      i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  download(id: string, name: string) {
    // Probably better to change to a API side download  as this provides
    // no real progress (We could add a spinner)
    this.dialog.open(SpinnerComponent, {
      height: '120px',
      width: '250px',
      data: {
        title: 'File Downloading'
      }
    });

    this.DocumentApi.findById(id).toPromise().then((response: any) => {
      window.open(response.data.url, '_blank', '');
      this.dialog.closeAll();
    }).catch(e => this.dialog.closeAll());
  }

  base64toBlob(base64Data: string, contentType: any) {
    contentType = contentType || '';
    const sliceSize = 1024;
    const byteCharacters = atob(base64Data);
    const bytesLength = byteCharacters.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays = new Array(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      const begin = sliceIndex * sliceSize;
      const end = Math.min(begin + sliceSize, bytesLength);

      const bytes = new Array(end - begin);
      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
    return new Blob(byteArrays, { type: contentType });
  }

  saveChanges(id: string, title: string, description: string) {

    this.DocumentApi.patch(id, {
      title: title,
      description: description,
    }).toPromise().then((response: any) => {
      this.notificationService.open('Document data updated.');
      const row = this.tempDatabase.data.find((doc: AssociatedDocument) => doc._id === id);
      if (row) {
        row.showSave = false;
      }
    }).catch((err) => {
      console.log('Error updating document:');
      console.log(err);
      this.notificationService.open('Document data was not updated.');
    });
  }

  public deleteDocument(item: AssociatedDocument) {
    const dialog = this.dialog.open(DeleteConfirmDialogComponent);
    dialog.afterClosed()
      .subscribe((confirm) => {
        if (confirm) {
          this.DocumentApi.deleteById(item._id, confirm).toPromise()
            .then((resp: any) => {
              if (resp.success) {
                this.notificationService.open(`Document ${item._id} was deleted`);
                this.tempDatabase.remove(item)
              }
            }).catch((err) => {
              console.log('Error updating document:');
              console.log(err);
              this.notificationService.open('Document data was not updated.');
            });
        }
      });
  }

  editTitle(row) {
    row.titleChange = true;
  }

  editDesc(row) {
    row.descChange = true;
  }
}
